/**
 * POLITIQUE D'ICÔNES — SOURCE UNIQUE (lot U2f, décision utilisateur 2026-11-03, option C).
 *
 * Règle à TROIS niveaux, et elle est tenue par ce seul fichier :
 *   N1 — en-tête de PAGE   : une icône, toujours, à gauche du titre ;
 *   N2 — carte de SECTION  : une icône, toujours (une section = une FONCTION :
 *        « Run of show », « Tasks », « My card »…) ;
 *   N3 — carte d'ENTITÉ    : JAMAIS d'icône (une personne, un crew, un groupe, un
 *        show, une salle, une tournée : leur NOM se suffit). Les cartes d'entité
 *        portent `.rc-card` / `.rc-item-card` + `.rc-card-name` et ne passent
 *        jamais par ce module ; la pastille de couleur d'une personne n'est pas
 *        une icône et reste.
 *
 * POURQUOI CE FICHIER EXISTE : les icônes vivaient dans les valeurs i18n. Trois
 * conséquences mesurées : le même pictogramme était à maintenir dans 3 locales,
 * un composant qui en ajoutait un second produisait « 📧 📧 Daysheet
 * reminders », et une icône ne se traduit pas alors qu'elle est recopiée par
 * traducteur. Ici : UNE table, aucune icône dans les fichiers de langue.
 *
 * RÉFÉRENCE DU LOT : `bandz/references/ui-densite-2026.md` §14.
 */

/** Icône d'un EN-TÊTE DE PAGE — clé = la clé i18n du titre. */
export const PAGE_ICONS: Readonly<Record<string, string>> = {
  'shows.title': '🎤',
  'calendar.title': '🗓',
  'itinerary.pageTitle': '🛣',
  'atlas.title': '🗺',
  'advancingHub.title': '📨',
  'tours.title': '🧭',
  'venues.title': '🏟️',
  'contacts.title': '📇',
  'bandSettings.title': '⚙️',
  'users.title': '👤',
  // Deux en-têtes distincts sur /api-keys (l'un est celui de la vue refusée).
  'apiKeys.title': '🔑',
  'apiKeys.titleIcon': '🔑',
  'account.title': '🔐',
  'aiAssistant.title': '🤖',
  // Configuration rider — l'icône vit ici, pas dans `config*.title`.
  'configSound.title': '🎛️',
  'configIem.title': '👂',
  'configHospitality.title': '🏨',
  'configLights.title': '💡',
  'configBackline.title': '🎒',
  'configGeneral.title': '📋',
  'configAudioSpecs.title': '🔊',
  'configStageDesign.title': '🖼',
  // ── LOT 3 (2026-11-04) — pages N1 qui n'avaient pas encore leur icône ────
  'team.title': '👥',                 // People (/tour/team)
  'musicians.title': '🎸',            // Musicians (/tour/musicians)
  'crew.title': '🎧',                 // Crew (/tour/crew)
  // Bands list — la clé du code est `bandList.title` ; `bands.title` est gardée
  // comme alias (le libellé « Bands » existe dans plusieurs écrans).
  'bands.title': '🎵',
  'bandList.title': '🎵',
  'stagePlotEditor.title': '🗺',      // Stage plots (en-tête `rc-header` maison)
  'payouts.title': '💶',              // Payouts
  // Disponibilité : la clé RÉELLE est `availabilityV2.pageTitle` (`pageTitleBand`
  // réutilise la même icône via cette clé de base).
  'availability.title': '📅',
  'availabilityV2.pageTitle': '📅',
  'myCalendar.title': '🗓',           // My calendar (/tour/calendar)
  'billing.title': '💳',              // Plans & subscription (`pricing.title` = alias)
  'pricing.title': '💳',
  'riderPage.title': '📋',            // Tech Rider (`rider.title` = alias)
  'rider.title': '📋',
  'riderEditor.title': '📐',          // Rider layout editor
  'daysheetLayout.title': '📐',
  'tourbookLayout.title': '📐',
  'layoutEditor.daysheetTitle': '📐', // clés réelles des 2 éditeurs de layout
  'layoutEditor.tourbookTitle': '📐',
  'dayOf.title': '⚡',                // Day of show
  'setlists.title': '🎵',
  'songbook.title': '🎼',
  'checklists.title': '✅',
  'help.title': '❓',
  'helpPage.title': '❓',
  // Tour Arcade (/games) — l'onglet mobile n°2 (LOT TOUR-ARCADE-GAMES, 2026-11-04).
  'games.title': '🕹️',
};

/**
 * Icône d'une CARTE DE SECTION — clé = `<pageId>:<slot>`.
 *
 * `pageId` est celui du `CardStack` (`users`, `account`, `team`,
 * `show_logistics`, `show_tech`, `show_advancing`) ou un identifiant de page
 * hors CardStack (`show-day`, `show-files`, `show-setlist`, `show-checklist`,
 * `daysheet` pour les sections rendues par `DaysheetViewer`).
 *
 * ⚠ Ne PAS ajouter une entrée « au feeling » : une carte absente d'ici reste
 * SANS icône (c'est la règle du lot) et se liste dans le handoff.
 */
export const CARD_ICONS: Readonly<Record<string, string>> = {
  // ── Users & Access ─────────────────────────────────────────────────────
  'users:table': '👥',            // Accounts ({{count}})
  'users:registry': '👥',         // People registry
  'users:filters': '🔎',          // Filters
  // ── Account ────────────────────────────────────────────────────────────
  'account:pw': '🔐',             // Change password
  'account:mycard': '🪪',         // My card
  'account:tech': '🪪',           // My technical card
  'account:docs': '📄',           // Documents
  'account:signedin': '🆔',       // Signed-in identity
  'account:sessions': '💻',       // Active sessions / Devices
  'account:twofa': '🛡',          // Two-factor authentication (2FA)
  'account:charts': '🎼',         // My song charts
  'account:avail': '📅',          // My availability
  'account:calendar': '🗓',       // My calendar
  'account:export': '📤',         // Export my data (GDPR)
  'account:delete': '🗑',         // Delete my account
  // ── Team (People) ──────────────────────────────────────────────────────
  'team:duplicates': '🧐',        // Possible duplicates
  'team:activity': '📈',          // Recent actions
  // ── Fiche show · onglet Day Sheet ──────────────────────────────────────
  'show-day:daysheet': '📋',      // Day Sheet
  'show-day:reminders': '📧',     // Daysheet reminders (UNE seule icône)
  'show-day:tasks': '☑️',         // Tasks
  'show-day:schedule': '⏱',       // Schedule (roadmap)
  'show-day:distances': '🛣',     // Distances
  'show-day:contacts': '👥',      // Contact persons
  // ── Day sheet LUE (DaysheetViewer, sections de la day sheet) ────────────
  'daysheet:general-info': 'ℹ️',
  'daysheet:venue': '🏟️',
  'daysheet:run-of-show': '🕒',
  'daysheet:distances': '🛣',
  'daysheet:contacts': '👥',
  'daysheet:notes': '📝',
  'daysheet:guestlist': '🎟',
  'daysheet:hotel': '🛏',
  // ── Fiche show · onglet Logistics ──────────────────────────────────────
  'show_logistics:guestlist': '🎟',
  'show_logistics:transport': '✈️',
  'show_logistics:accommodation': '🛏',
  'show_logistics:notes': '📝',
  // ── Fiche show · onglet Tech ───────────────────────────────────────────
  'show_tech:rider': '🎧',
  'show_tech:providers': '🎒',
  'show_tech:roles': '🎭',
  'show_tech:notes': '📝',
  // ── Fiche show · onglet Advancing ──────────────────────────────────────
  // LOT 3 (2026-11-04) : les 6 cartes de SECTION de l'onglet Advancing.
  'show_advancing:rider': '🎧',
  'show_advancing:progress': '📊',
  'show_advancing:email': '📧',   // Daysheet reminders (même fonction que show-day:reminders)
  'show_advancing:overview': '📋',
  'show_advancing:settings': '⚙️',
  'show_advancing:shares': '🔗',
  'show_advancing:history': '🕘',
  // ── Fiche show · onglet Finance ────────────────────────────────────────
  'show_finance:finance': '💰',
  // ── Users & Access — la carte « Add an account » ───────────────────────
  'users:add': '➕',
  // ── API Keys — les deux pastilles de liste (Active / Revoked) ──────────
  'apiKeys:active': '🔑',
  'apiKeys:revoked': '🚫',
  // ── Fiche show · Files / Setlist / Checklist ───────────────────────────
  'show-files:files': '📁',
  'show-setlist:setlist': '🎵',
  'show-checklist:checklist': '✅',
};

// Un pictogramme de tête (« ⚙️ » = U+2699 + FE0F) et rien d'autre.
const LEADING_ICON = /^(?:\p{Extended_Pictographic}[\uFE0F\u200D\s]*)+/u;

/**
 * Retire une icône déjà présente en tête d'un libellé. C'est la GARDE
 * anti-doublon : un libellé qui arrive avec son pictogramme (i18n pas encore
 * nettoyée, donnée saisie par un utilisateur) ne peut pas produire « 📧 📧 ».
 */
export function stripLeadingIcon(label: string): string {
  return label.replace(LEADING_ICON, '');
}

/** Titre de PAGE : l'icône de la table, sinon le libellé tel quel. */
export function withPageIcon(key: string, label: string): string {
  const icon = PAGE_ICONS[key];
  if (!icon) return label;
  return icon + ' ' + stripLeadingIcon(label);
}

/** Titre de CARTE DE SECTION : l'icône du slot `<pageId>:<slot>`, sinon tel quel. */
export function withCardIcon(slot: string, label: string): string {
  const icon = CARD_ICONS[slot];
  if (!icon) return label;
  return icon + ' ' + stripLeadingIcon(label);
}
